import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ArrowRight, MapPin, Loader } from 'lucide-react';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import Watermark from '../components/Watermark';

const PlaceOfBirth = () => {
  const navigate = useNavigate();
  const [city, setCity] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Use free OpenStreetMap Nominatim API for geocoding
  const handleCityChange = async (value) => {
    setCity(value);
    
    if (value.trim().length > 2) {
      setIsLoading(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&format=json&limit=5&addressdetails=1`,
          { headers: { 'User-Agent': 'TaraanayaApp' } }
        );
        
        if (response.ok) {
          const data = await response.json();
          console.log('Nominatim results:', data.length);
          setPredictions(data.map(item => ({
            place_id: item.place_id,
            description: item.display_name,
            lat: parseFloat(item.lat),
            lon: parseFloat(item.lon)
          })));
        } else {
          setPredictions([]);
        }
      } catch (error) {
        console.error('Geocoding error:', error);
        setPredictions([]);
      } finally {
        setIsLoading(false);
      }
    } else {
      setPredictions([]);
    }
  };

  const handleSelectPlace = (prediction) => {
    setCity(prediction.description);
    setPredictions([]);
    
    const coords = {
      latitude: prediction.lat,
      longitude: prediction.lon,
      place_name: prediction.description
    };
    setSelectedPlace(coords);
    console.log('Selected coordinates:', coords);
    toast.success('✓ Location found!');
  };

  const handleContinue = () => {
    if (!city.trim()) {
      toast.error('Please enter your birth place');
      return;
    }
    
    if (selectedPlace) {
      localStorage.setItem('placeOfBirth', city.trim());
      localStorage.setItem('birthCoordinates', JSON.stringify(selectedPlace));
      navigate('/date-of-birth');
    } else {
      toast.info('Using default coordinates');
      localStorage.setItem('placeOfBirth', city.trim());
      localStorage.setItem('birthCoordinates', JSON.stringify({
        latitude: 28.6139,
        longitude: 77.2090,
        place_name: city.trim()
      }));
      navigate('/date-of-birth');
    }
  };

  return (
    <CosmicBackground showAstroChart={true}>
      <Watermark position="bottom-right" opacity={0.3} />
      
      <div className="min-h-screen relative">
        <div className="absolute top-14 left-0 right-0 flex gap-1 px-4">
          <div className="flex-1 h-1 bg-[#D4AF37] rounded-full" />
          <div className="flex-1 h-1 bg-[#D4AF37] rounded-full" />
          <div className="flex-1 h-1 bg-gray-600 rounded-full" />
          <div className="flex-1 h-1 bg-gray-600 rounded-full" />
          <div className="flex-1 h-1 bg-gray-600 rounded-full" />
        </div>

        <button onClick={() => navigate(-1)} className="absolute top-14 left-4 text-white z-20">
          <ChevronLeft className="w-6 h-6" />
        </button>

        <div className="flex flex-col h-screen pt-24 px-6">
          <h1 className="text-white text-3xl font-serif text-center mb-3" style={{letterSpacing: '0.1em'}}>
            PLACE OF BIRTH
          </h1>

          <p className="text-gray-400 text-center text-sm mb-8 px-4 flex items-center justify-center gap-2">
            {isLoading && <Loader className="w-4 h-4 animate-spin" />}
            Type your city name (e.g., Mumbai, India)
          </p>

          <div className="mb-4 relative">
            <label className="text-gray-300 text-sm mb-2 block">Enter your City</label>
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D4AF37]" />
              <input
                type="text"
                value={city}
                onChange={(e) => handleCityChange(e.target.value)}
                placeholder="e.g., Mumbai, India"
                className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] transition-colors"
                autoFocus
              />
            </div>
            
            {predictions.length > 0 && (
              <div className="absolute z-10 w-full mt-2 bg-gray-900/95 backdrop-blur-sm border border-white/20 rounded-xl overflow-hidden max-h-64 overflow-y-auto shadow-2xl">
                {predictions.map((prediction) => (
                  <button
                    key={prediction.place_id}
                    onClick={() => handleSelectPlace(prediction)}
                    className="w-full px-4 py-3 text-left text-white hover:bg-white/10 transition-colors flex items-start gap-2 border-b border-white/10 last:border-0"
                  >
                    <MapPin className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{prediction.description}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedPlace && (
            <div className="mb-4 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-400 text-xs flex items-center gap-2">
                <span>✓</span>
                Coordinates: {selectedPlace.latitude.toFixed(4)}, {selectedPlace.longitude.toFixed(4)}
              </p>
            </div>
          )}

          <div className="flex-1" />

          <button
            onClick={handleContinue}
            disabled={!city.trim()}
            className="w-full py-4 bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-[#0a1628] rounded-full font-semibold text-lg flex items-center justify-center gap-2 mb-8 disabled:opacity-50 hover:scale-105 transition-transform shadow-lg"
          >
            Continue
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </CosmicBackground>
  );
};

export default PlaceOfBirth;
